/**
 * 复制到剪贴板工具函数
 * 支持多种浏览器和环境的降级方案
 */

/**
 * 复制文本到剪贴板
 * @param {string} text - 要复制的文本
 * @param {Object} options - 配置选项
 * @param {Function} options.onSuccess - 成功回调函数
 * @param {Function} options.onError - 失败回调函数
 * @param {string} options.successMessage - 成功提示消息（默认：'已复制到剪贴板'）
 * @param {string} options.errorMessage - 失败提示消息（默认：'复制失败，请手动复制'）
 * @returns {Promise} Promise 对象
 */
export function copyToClipboard(text, options = {}) {
  if (!text) {
    const errorMsg = options.errorMessage || '复制失败：内容为空'
    if (options.onError) {
      options.onError(new Error('内容为空'))
    } else {
      console.warn(errorMsg)
    }
    return Promise.reject(new Error('内容为空'))
  }

  const {
    onSuccess,
    successMessage = '已复制到剪贴板'
  } = options

  // 方法1：使用现代 Clipboard API（推荐）
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard
      .writeText(text)
      .then(() => {
        if (onSuccess) {
          onSuccess(text)
        } else {
          console.log(successMessage)
        }
        return text
      })
      .catch((err) => {
        console.warn('Clipboard API 失败，使用降级方案:', err)
        // 降级到方法2，传递完整的 options 对象
        return fallbackCopyToClipboard(text, options)
      })
  } else {
    // 方法2：使用降级方案，传递完整的 options 对象
    return fallbackCopyToClipboard(text, options)
  }
}

/**
 * 降级复制方案（兼容旧浏览器和非 HTTPS 环境）
 * @param {string} text - 要复制的文本
 * @param {Object} options - 配置选项
 * @returns {Promise} Promise 对象
 */
function fallbackCopyToClipboard(text, options = {}) {
  return new Promise((resolve, reject) => {
    try {
      const textArea = document.createElement('textarea')
      textArea.value = text
      
      // 设置样式，避免影响页面布局
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      textArea.style.opacity = '0'
      textArea.style.pointerEvents = 'none'
      
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      
      // 兼容 iOS Safari
      if (navigator.userAgent.match(/ipad|ipod|iphone/i)) {
        const range = document.createRange()
        range.selectNodeContents(textArea)
        const selection = window.getSelection()
        selection.removeAllRanges()
        selection.addRange(range)
        textArea.setSelectionRange(0, 999999)
      }
      
      const successful = document.execCommand('copy')
      document.body.removeChild(textArea)
      
      if (successful) {
        const { onSuccess, successMessage = '已复制到剪贴板' } = options
        if (onSuccess) {
          onSuccess(text)
        } else {
          console.log(successMessage)
        }
        resolve(text)
      } else {
        throw new Error('execCommand copy 失败')
      }
    } catch (err) {
      console.error('复制失败:', err)
      const { onError, errorMessage = '复制失败，请手动复制' } = options
      
      if (onError) {
        onError(err)
      } else {
        // 如果所有方法都失败，显示提示让用户手动复制
        const userMessage = `${errorMessage}\n\n请手动复制以下内容：\n${text}`
        if (window.confirm && window.confirm(userMessage)) {
          // 用户点击确定后，可以尝试显示文本
          console.log('需要复制的内容:', text)
        } else {
          console.info(userMessage)
        }
      }
      reject(err)
    }
  })
}

/**
 * Vue 组件中使用的便捷方法
 * 支持 Element UI 的 $message 或其他消息提示组件
 * @param {string} text - 要复制的文本
 * @param {Object} vueInstance - Vue 组件实例（可选，用于调用 $message）
 * @param {string} successMessage - 成功提示消息
 * @param {string} errorMessage - 失败提示消息
 * @returns {Promise} Promise 对象
 */
export function copyToClipboardWithMessage(text, vueInstance = null, successMessage = '已复制到剪贴板', errorMessage = '复制失败，请手动复制') {
  return copyToClipboard(text, {
    onSuccess: () => {
      if (vueInstance && vueInstance.$message && vueInstance.$message.success) {
        vueInstance.$message.success(successMessage)
      } else if (vueInstance && vueInstance.showMessage) {
        vueInstance.showMessage(successMessage, 'success')
      } else {
        console.info(successMessage)
      }
    },
    onError: (err) => {
      if (vueInstance && vueInstance.$message && vueInstance.$message.error) {
        vueInstance.$message.error(errorMessage)
      } else if (vueInstance && vueInstance.showMessage) {
        vueInstance.showMessage(errorMessage, 'error')
      } else {
        console.error(errorMessage)
      }
      console.error('复制失败:', err)
    },
    successMessage,
    errorMessage
  })
}

/**
 * @param {string} text - 要复制的文本
 * @param {string} successMessage - 成功提示消息
 * @returns {Promise} Promise 对象
 */
export function copyToClipboardSimple(text, successMessage = '已复制') {
  return copyToClipboard(text, {
    onSuccess: () => {
      this.$message.success(successMessage)
    },
    onError: (err) => {
      console.error(`复制失败，请手动复制：\n${text}`)
      console.error('复制失败:', err)
    }
  })
}

/**
 * 将 HTML 转换为纯文本，保留换行格式
 * @param {string} htmlStr - HTML 字符串
 * @returns {string} 纯文本字符串
 */
function htmlToText(htmlStr) {
  if (!htmlStr) return '';
  // 如果已经是纯文本，直接返回
  if (!/<[^>]*>/.test(htmlStr)) {
    return htmlStr;
  }
  
  // 先处理 HTML 字符串，将块级元素和换行标签转换为换行符
  let processedHtml = htmlStr
    // 将 <br>、<br/>、<br /> 替换为换行符（使用特殊标记，避免被后续处理影响）
    .replace(/<br\s*\/?>/gi, '\n')
    // 将块级元素的结束标签后添加换行符
    .replace(/<\/(p|div|li|h[1-6]|tr|pre)>/gi, '\n')
    // 将块级元素的开始标签前添加换行符（如果前面没有换行）
    .replace(/([^\n])<(p|div|li|h[1-6]|tr|pre)[^>]*>/gi, '$1\n');
  
  // 创建一个临时的 div 元素来解析 HTML
  const div = document.createElement('div');
  div.innerHTML = processedHtml;
  
  // 使用 innerText 获取纯文本（它会自动处理换行，将块级元素转换为换行）
  let text = div.innerText || div.textContent || '';
  
  // 进一步处理：确保换行格式正确
  // 移除多余的连续换行（保留最多两个连续换行，表示段落间距）
  text = text.replace(/\n{3,}/g, '\n\n');
  
  // 清理每行首尾的空格，但保留换行
  text = text.split('\n').map(line => line.trim()).join('\n');
  
  // 移除首尾空白
  text = text.trim();
  
  return text;
}

/**
 * 判断是否为富文本（HTML）内容
 * @param {string} content - 内容字符串
 * @returns {boolean} 是否为富文本
 */
function isRichText(content) {
  if (!content) return false;
  return /<[^>]*>/.test(content);
}

/**
 * 使用 textarea 复制文本并保留换行（降级方案，兼容 HTTP 环境）
 * @param {string} text - 要复制的文本
 * @param {Object} options - 配置选项
 * @returns {Promise} Promise 对象
 */
function copyTextWithNewlines(text, options = {}) {
  return new Promise((resolve, reject) => {
    try {
      // 确保文本中的换行符是正确的 \n（textarea 需要 \n，不需要 \r\n）
      // 但不要改变文本内容，只是确保格式正确
      const normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      
      const textarea = document.createElement('textarea');
      textarea.value = normalizedText;
      // 设置样式，确保不影响页面布局，但需要可见才能复制
      textarea.style.position = 'fixed';
      textarea.style.left = '0';
      textarea.style.top = '0';
      textarea.style.width = '2em';
      textarea.style.height = '2em';
      textarea.style.padding = '0';
      textarea.style.border = 'none';
      textarea.style.outline = 'none';
      textarea.style.boxShadow = 'none';
      textarea.style.background = 'transparent';
      textarea.style.opacity = '0';
      textarea.style.zIndex = '-1';
      textarea.setAttribute('readonly', '');
      textarea.setAttribute('aria-hidden', 'true');
      
      document.body.appendChild(textarea);
      
      // 选择文本（多种方式确保兼容性）
      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, normalizedText.length);
      
      // 兼容 iOS Safari
      if (navigator.userAgent.match(/ipad|ipod|iphone/i)) {
        const range = document.createRange();
        range.selectNodeContents(textarea);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        textarea.setSelectionRange(0, normalizedText.length);
      }
      
      const successful = document.execCommand('copy');
      
      // 清理
      document.body.removeChild(textarea);
      
      if (successful) {
        const { onSuccess, successMessage = '已复制到剪贴板' } = options;
        if (onSuccess) {
          onSuccess(normalizedText);
        } else {
          console.log(successMessage);
        }
        resolve(normalizedText);
      } else {
        throw new Error('execCommand copy 失败');
      }
    } catch (err) {
      console.error('复制失败:', err);
      const { onError, errorMessage = '复制失败，请手动复制' } = options;
      if (onError) {
        onError(err);
      } else {
        console.error(errorMessage);
      }
      reject(err);
    }
  });
}

/**
 * 复制富文本内容到剪贴板，保留换行格式
 * 支持 HTML 和纯文本，自动处理换行格式
 * @param {string} content - 要复制的内容（可以是 HTML 或纯文本）
 * @param {Object} options - 配置选项
 * @param {Function} options.onSuccess - 成功回调函数
 * @param {Function} options.onError - 失败回调函数
 * @param {string} options.successMessage - 成功提示消息（默认：'已复制到剪贴板'）
 * @param {string} options.errorMessage - 失败提示消息（默认：'复制失败，请手动复制'）
 * @param {Object} options.vueInstance - Vue 组件实例（可选，用于调用 $message）
 * @returns {Promise} Promise 对象
 */
export function copyRichTextToClipboard(content, options = {}) {
  if (!content) {
    const errorMsg = options.errorMessage || '复制失败：内容为空';
    if (options.onError) {
      options.onError(new Error('内容为空'));
    } else {
      console.warn(errorMsg);
    }
    return Promise.reject(new Error('内容为空'));
  }

  const {
    onSuccess,
    onError,
    successMessage = '已复制到剪贴板',
    errorMessage = '复制失败，请手动复制',
    vueInstance = null
  } = options;

  // 将 HTML 转换为纯文本，保留换行格式
  const plainText = htmlToText(content);
  const isHTML = isRichText(content);

  // 处理成功提示
  const handleSuccess = () => {
    if (onSuccess) {
      onSuccess(plainText);
    } else if (vueInstance) {
      if (vueInstance.$message && vueInstance.$message.success) {
        vueInstance.$message.success(successMessage);
      } else if (vueInstance.showMessage) {
        vueInstance.showMessage(successMessage, 'success');
      } else {
        console.info(successMessage);
      }
    }
  };

  // 处理失败提示
  const handleError = (err) => {
    if (onError) {
      onError(err);
    } else if (vueInstance) {
      if (vueInstance.$message && vueInstance.$message.error) {
        vueInstance.$message.error(errorMessage);
      } else if (vueInstance.showMessage) {
        vueInstance.showMessage(errorMessage, 'error');
      }
    }
    console.error('复制失败:', err);
  };

  // 检查是否在安全上下文中（HTTPS 或 localhost）
  const isSecureContext = window.isSecureContext || location.protocol === 'https:' || location.hostname === 'localhost' || location.hostname === '127.0.0.1';
  
  // 尝试使用 ClipboardItem API（支持 HTML 和纯文本）
  // 注意：ClipboardItem API 需要安全上下文，在 HTTP 环境下可能不可用
  if (isSecureContext && navigator.clipboard && navigator.clipboard.write) {
    try {
      if (isHTML) {
        // 如果内容是 HTML，同时提供 HTML 和纯文本版本
        const htmlBlob = new Blob([content], { type: 'text/html' });
        const textBlob = new Blob([plainText], { type: 'text/plain' });
        const item = new ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': textBlob
        });

        return navigator.clipboard.write([item])
          .then(() => {
            handleSuccess();
            return plainText;
          })
          .catch((err) => {
            console.log('ClipboardItem API 失败，降级到 textarea 方案:', err);
            // 降级到纯文本复制（使用 textarea，确保保留换行）
            return copyTextWithNewlines(plainText, { onSuccess: handleSuccess, onError: handleError, successMessage, errorMessage });
          });
      } else {
        // 纯文本，直接使用 writeText
        return navigator.clipboard.writeText(plainText)
          .then(() => {
            handleSuccess();
            return plainText;
          })
          .catch((err) => {
            console.log('writeText API 失败，降级到 textarea 方案:', err);
            // 降级到 textarea 方案（确保保留换行）
            return copyTextWithNewlines(plainText, { onSuccess: handleSuccess, onError: handleError, successMessage, errorMessage });
          });
      }
    } catch (err) {
      console.log('Clipboard API 异常，降级到 textarea 方案:', err);
      
      // 如果 ClipboardItem 不可用，降级到纯文本复制（使用 textarea）
      return copyTextWithNewlines(plainText, { onSuccess: handleSuccess, onError: handleError, successMessage, errorMessage });
    }
  } else {
    // HTTP 环境或 Clipboard API 不可用，直接使用 textarea 方案（确保保留换行）
    console.log('使用 textarea 方案（HTTP 环境或不支持 Clipboard API）');
    return copyTextWithNewlines(plainText, { onSuccess: handleSuccess, onError: handleError, successMessage, errorMessage });
  }
}

/**
 * Vue 组件中使用的便捷方法，复制富文本内容
 * @param {string} content - 要复制的内容（可以是 HTML 或纯文本）
 * @param {Object} vueInstance - Vue 组件实例（可选，用于调用 $message）
 * @param {string} successMessage - 成功提示消息
 * @param {string} errorMessage - 失败提示消息
 * @returns {Promise} Promise 对象
 */
export function copyRichTextWithMessage(content, vueInstance = null, successMessage = '已复制到剪贴板', errorMessage = '复制失败，请手动复制') {
  return copyRichTextToClipboard(content, {
    vueInstance,
    successMessage,
    errorMessage
  });
}

